import { FetchNextPageOptions, InfiniteQueryObserverResult } from '@tanstack/react-query'
import classNames from 'classnames'
import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'
import Parser from 'react-html-parser'
import { HIGHLIGHT_TAG_MAP, HighlightItem } from '../../../../../model/caf.interface'
import useAppRouter from '../../../../../modules/hooks/common/useAppRouter'
import useIsMobile from '../../../../../modules/hooks/common/useIsMobile'
import useIsWebview from '../../../../../modules/hooks/common/useIsWebview'
import useHtmlParserTransform from '../../../../../modules/hooks/webview/caf/common/useHtmlParserTransform'
import useMapTagToWording from '../../../../../modules/hooks/webview/caf/common/useMapTagToWording'
import useWebviewNavigation from '../../../../../modules/hooks/webview/common/useWebviewNavigation'
import { n_caf } from '../../../../../modules/i18n/strings/auto/wording_webview'
import useTranslation from '../../../../../modules/i18n/useTranslation'
import { IC_CAF_ALERT_TRIANGLE, IC_CAF_ERROR_CIRCLE } from '../../../../../modules/images'
import urls from '../../../../../modules/urls'
import { LESSON_ACTIVITIES_MAP, useLessonsStack } from '../../../../../pages/[lang]/webview/lessons'
import SimpleLineChart from '../../charts/SimpleLineChart'
import ViewMoreButton from '../../common/ViewMoreButton'
import HighSumCardTemplate from '../../highlight/HighSumCardTemplate'

interface IHighlightListSectionProps {
  data: any[]
  tag: number
  highlightsCount: number
  hasNext: number
  isFetchingNextPage: boolean
  fetchNextPage: (options?: FetchNextPageOptions) => Promise<InfiniteQueryObserverResult>
  className?: string
  showViewMoreButton?: boolean
  isNotAverage?: boolean
}

const HighlightListSection = ({
  data,
  tag,
  highlightsCount,
  hasNext,
  isFetchingNextPage,
  fetchNextPage,
  className,
  showViewMoreButton = true,
  isNotAverage = false,
}: IHighlightListSectionProps) => {
  const { t } = useTranslation()
  const { countWordingPerTag } = useMapTagToWording(isNotAverage)
  const { navigate } = useWebviewNavigation()
  const { useLessonsFlow } = useLessonsStack()
  const { push } = useLessonsFlow()
  const router = useAppRouter({ stackPush: push })

  const isWebview = useIsWebview()
  const isMobile = useIsMobile()
  const isStatistic = router.asPath.includes('/portal/lessons/caf') || router.asPath.includes('/my-ringle/ai-analysis')
  const goHighlights = (highlightId, tagId) => {
    if (isWebview) {
      push(LESSON_ACTIVITIES_MAP.AIHighlightDetailActivity, { highlightId, tag: tagId })
    } else if (!isMobile && isStatistic) {
      const { tab, startDate, endDate } = router.query
      router.push(urls.student.newPortal.myRingle.AIAnalysis.asHighlightId(tab, startDate, endDate, tag, highlightId))
    } else {
      const url = urls.webview.caf.highlight.detail(highlightId, tagId)
      navigate({ url })
    }
  }
  const count = (item) => {
    return item.count
  }

  if (isMobile) {
    return (
      <>
        <h2 className={styles2.title}>{t(n_caf.statistic.highlight.count(highlightsCount))}</h2>
        <ul>
          {data?.map((highItem, idx) => {
            const hasContent = count(highItem) > 0
            return (
              <HighSumCardTemplate
                title={highItem.formatted_start_time}
                subtitle={hasContent ? countWordingPerTag(tag, count(highItem)).count : countWordingPerTag(tag).noCount}
                bottomSection={<BottomSection tag={tag} highItem={highItem} />}
                key={idx + 'highlight'}
                disabled={!hasContent}
                onClick={() => goHighlights(highItem.id, tag)}
                className={className}
              />
            )
          })}
        </ul>
        {hasNext && showViewMoreButton && (
          <div className="flex w-full cursor-pointer flex-row justify-center py-[16px]">
            <ViewMoreButton handleClick={fetchNextPage} isFetchingNextPage={isFetchingNextPage} />
          </div>
        )}
      </>
    )
  } else {
    return (
      <>
        <h2 className={styles2.title_pc}>{t(n_caf.statistic.highlight.count(highlightsCount))}</h2>
        <ul>
          {data?.map((highItem, idx) => {
            const hasContent = count(highItem) > 0
            return (
              <HighSumCardTemplate
                title={highItem.formatted_start_time}
                subtitle={hasContent ? countWordingPerTag(tag, count(highItem)).count : countWordingPerTag(tag).noCount}
                bottomSection={<BottomSection tag={tag} highItem={highItem} />}
                key={idx + 'highlight'}
                disabled={!hasContent}
                onClick={() => goHighlights(highItem.id, tag)}
                className={className}
              />
            )
          })}
        </ul>
        {hasNext && showViewMoreButton && (
          <div className="flex w-full cursor-pointer flex-row justify-center py-[16px]">
            <ViewMoreButton handleClick={fetchNextPage} isFetchingNextPage={isFetchingNextPage} />
          </div>
        )}
      </>
    )
  }
}

export default HighlightListSection

const BottomSection = ({ tag, highItem }) => {
  const componentList = {
    else: <SentenceExample data={highItem.details?.sentence || highItem.details?.my_sentence} type="error" />,
    [HIGHLIGHT_TAG_MAP['repetition']]: <WordList data={highItem.details} />,
    [HIGHLIGHT_TAG_MAP['pronunciation']]: <WordList data={highItem.details} />,
    [HIGHLIGHT_TAG_MAP['ros']]: <SentenceExample data={highItem.details?.sentence} type="runon" />,
    [HIGHLIGHT_TAG_MAP['speech_rate']]: <SpeechDetail detail={highItem.details} />,
    [HIGHLIGHT_TAG_MAP['filler_word']]: (
      <SentenceExample data={highItem.details?.sentence || highItem.details || ''} type="filler" />
    ),
    [HIGHLIGHT_TAG_MAP['sentence_structure']]: (
      <SentenceStructureDetail data={highItem.details?.sentence || highItem.details || ''} />
    ),
  }
  return componentList[tag] ?? componentList.else
}

const styles2 = {
  title: `text-h5 mt-[24px] mb-[16px] text-gray-700`,
  title_pc: `text-h5 mb-[16px] text-gray-700`,
}
const SentenceExample = ({ data, type = 'error' }) => {
  const { transformHTMLNode } = useHtmlParserTransform()
  const isMobile = useIsMobile()
  if (!data) return null
  return isMobile ? (
    <div className="flex w-full items-start gap-[10px] rounded-[6px] bg-gray-100 p-14">
      {type !== 'filler' && (
        <img src={type === 'error' ? IC_CAF_ERROR_CIRCLE : IC_CAF_ALERT_TRIANGLE} width={16} height={16} alt="alert" />
      )}
      <span className="line-clamp-3 text-[14px] font-medium leading-[20px] tracking-tight text-black">
        {Parser(data, {
          transform: transformHTMLNode,
        })}
      </span>
    </div>
  ) : (
    <div className="flex h-full w-full items-start gap-[10px] rounded-[6px] bg-gray-50 p-12">
      {type !== 'filler' && (
        <img src={type === 'error' ? IC_CAF_ERROR_CIRCLE : IC_CAF_ALERT_TRIANGLE} width={16} height={16} alt="alert" />
      )}
      <span className="line-clamp-3 text-[14px] font-medium leading-[20px] tracking-tight text-black">
        {Parser(data, {
          transform: transformHTMLNode,
        })}
      </span>
    </div>
  )
}

const SentenceStructureDetail = ({ data }) => {
  const { transformHTMLNode } = useHtmlParserTransform()
  const isMobile = useIsMobile()
  if (!data) return null
  return isMobile ? (
    <div className="flex w-full items-start gap-[10px] px-[16px] pt-[12px] pb-[16px]">
      <span className="line-clamp-3 text-[14px] font-medium leading-[20px] tracking-tight text-black">
        {Parser(data, {
          transform: transformHTMLNode,
        })}
      </span>
    </div>
  ) : (
    <div className="flex w-full items-start gap-[10px] bg-gray-50 px-[12px] pt-[12px] pb-[12px]">
      <span className="line-clamp-2 text-[14px] font-medium tracking-tight text-black">
        {Parser(data, {
          transform: transformHTMLNode,
        })}
      </span>
    </div>
  )
}

const WordList = ({ data }) => {
  const isMobile = useIsMobile()
  if (!Array.isArray(data)) return null
  return isMobile ? (
    <div className="flex w-full flex-wrap gap-x-[8px] gap-y-[12px] overflow-hidden">
      {data?.map((item) => (
        <div key={item} className="rounded-full bg-gray-100 px-12 py-4">
          <span className="text-h4 leading-[23px] text-purple-500">{item}</span>
        </div>
      ))}
    </div>
  ) : (
    <div className="flex w-full flex-wrap gap-x-[8px] gap-y-[12px] overflow-hidden">
      {data?.map((item) => (
        <div key={item} className="rounded-full bg-gray-100 px-12 py-4">
          <span className="text-[16px] font-normal leading-[23px] text-purple-500">{item}</span>
        </div>
      ))}
    </div>
  )
}

const SpeechDetail = ({ detail }: { detail: HighlightItem['details'] }) => {
  const chartWrapRef = useRef<HTMLDivElement>(null)
  const { t } = useTranslation()
  const [chartWidth, setChartWidth] = useState(0)
  const [chartHeight, setChartHeight] = useState(0)
  const router = useRouter()
  const isStatistic = router.asPath.includes('/portal/lessons/caf')
  useEffect(() => {
    if (chartWrapRef.current) {
      const width = chartWrapRef.current.offsetWidth
      const height = chartWrapRef.current.offsetHeight
      setChartWidth(width)
      setChartHeight(height)
    }
  }, [chartWrapRef])
  const isMobile = useIsMobile()
  if (!detail?.graph_data) return null
  return isMobile ? (
    <div className="flex h-[52px] w-full flex-1 justify-between overflow-hidden">
      <div className="text-14 flex flex-shrink-0 items-end font-bold text-gray-300">
        {t(n_caf.highlight.fraction_counts(detail?.detail_count, detail?.total_count))}
      </div>
      <div className="flex flex-1 items-center justify-end">
        <div className="max-w-[216px] flex-1" ref={chartWrapRef}>
          <SimpleLineChart
            width={chartWidth}
            height={chartHeight}
            data={detail.graph_data}
            dataKey="wpm"
            indexBy="sentence"
          />
        </div>
      </div>
    </div>
  ) : (
    <div className="flex w-full flex-1 justify-between overflow-hidden">
      <div className="text-14 flex flex-shrink-0 items-end font-bold text-gray-300">
        {t(n_caf.highlight.fraction_counts(detail?.detail_count, detail?.total_count))}
      </div>
      <div className="flex flex-1 items-center justify-end">
        <div
          className={classNames('flex-1', {
            'h-[52px] max-w-[216px]': !isStatistic,
            'h-[90px] max-w-[296px]': isStatistic,
          })}
          ref={chartWrapRef}
        >
          <SimpleLineChart
            width={chartWidth}
            height={chartHeight}
            data={detail.graph_data}
            dataKey="wpm"
            indexBy="sentence"
          />
        </div>
      </div>
    </div>
  )
}

const styles = {
  highlight: {
    item: {
      box: `flex w-full items-end justify-between font-bold`,
      left: {
        count: `mr-[2px] text-[20px] leading-[24px] text-gray-700`,
        unit: `text-[14px] text-gray-300`,
      },
      right: {
        graph: `min-w-[70%]`,
        speechRate: `flex min-w-[70%] items-center`,
      },
    },
  },
}
